/** Canonical 40-item interest questionnaire slug (keep in sync with config/productVectors.js). */
export const DEFAULT_INTEREST_PAPER_SLUG = 'interest-test';

/**
 * @param {Array<{ slug?: string }>} papers
 */
export function pickDefaultInterestPaper(papers) {
  if (!Array.isArray(papers) || papers.length === 0) return null;
  return papers.find((p) => p.slug === DEFAULT_INTEREST_PAPER_SLUG) || papers[0];
}

/**
 * @param {{ en?: string, tc?: string, sc?: string } | null | undefined} obj
 * @param {string} lang
 */
export function pickLocalized(obj, lang) {
  if (!obj || typeof obj !== 'object') return '';
  const l = lang === 'en' || lang === 'tc' || lang === 'sc' ? lang : 'en';
  return obj[l] || obj.en || obj.tc || obj.sc || '';
}

/**
 * Ability papers use a single continuous question flow (no per-section picker).
 * @param {object | null | undefined} _paper
 */
export function paperUsesSectionMenu(_paper) {
  return false;
}

/**
 * Papers with multiple sections show a cover/buffer screen before each section.
 * @param {object | null | undefined} paper
 */
export function paperUsesSectionCovers(paper) {
  return getSectionsWithQuestions(paper).length >= 2;
}

/**
 * @param {object | null | undefined} paper
 */
export function getPrimarySection(paper) {
  return (paper?.sections || []).find((section) => (section.questions || []).length > 0) ?? null;
}

/**
 * @param {object} paper
 * @returns {{ section: object, question: object }[]}
 */
export function flattenPaperQuestions(paper) {
  const items = [];
  for (const section of paper.sections || []) {
    for (const question of section.questions || []) {
      items.push({ section, question });
    }
  }
  return items;
}

/**
 * @param {object} section
 * @returns {{ section: object, question: object }[]}
 */
export function flattenSectionQuestions(section) {
  const items = [];
  for (const question of section?.questions || []) {
    items.push({ section, question });
  }
  return items;
}

/**
 * @param {object} paper
 * @param {string} sectionId
 */
export function getSectionById(paper, sectionId) {
  return (paper?.sections || []).find((s) => s.id === sectionId) || null;
}

/**
 * Sections that contain at least one question.
 * @param {object | null | undefined} paper
 */
export function getSectionsWithQuestions(paper) {
  return (paper?.sections || []).filter((section) => (section.questions || []).length > 0);
}

/**
 * @param {object | null | undefined} paper
 * @param {string | null | undefined} sectionId
 */
export function getSectionIndex(paper, sectionId) {
  if (!sectionId) return -1;
  return getSectionsWithQuestions(paper).findIndex((section) => section.id === sectionId);
}

/**
 * @param {object | null | undefined} paper
 * @param {string | null | undefined} currentSectionId
 */
export function getNextSection(paper, currentSectionId) {
  const sections = getSectionsWithQuestions(paper);
  const idx = getSectionIndex(paper, currentSectionId);
  if (idx < 0 || idx >= sections.length - 1) return null;
  return sections[idx + 1];
}

/**
 * @param {object} paper
 * @param {number} globalIndex
 * @returns {{ sectionId: string | null, questionIndex: number }}
 */
export function globalQuestionIndexToSection(paper, globalIndex) {
  let offset = 0;
  for (const section of paper?.sections || []) {
    const n = (section.questions || []).length;
    if (n === 0) continue;
    if (globalIndex < offset + n) {
      return { sectionId: section.id, questionIndex: globalIndex - offset };
    }
    offset += n;
  }
  const first = paper?.sections?.[0];
  return { sectionId: first?.id ?? null, questionIndex: 0 };
}

/**
 * @param {object} section
 * @param {Record<string, string>} answers
 */
export function countAnsweredInSection(section, answers) {
  return (section?.questions || []).filter((q) => answers[q.id] != null).length;
}

/**
 * @param {object} paper
 * @param {Record<string, string>} answers
 */
export function countAnsweredInPaper(paper, answers) {
  return flattenPaperQuestions(paper).filter(({ question }) => answers[question.id] != null).length;
}

export const ABILITY_PAPER_MENU_GUIDE_KEY = 'edvise.abilityPaperMenuGuideSeen';

/**
 * Masonry-style span pattern for paper menu tiles.
 * Spans are chosen so each row uses the full column count (2 on mobile, 4 on md+).
 * @param {number} index
 * @param {number} total
 * @param {number} columns - active grid column count (2 or 4)
 */
export function getPaperTileSpan(index, total = 1, columns = 2) {
  const cap = (span) => ({
    ...span,
    colSpan: Math.min(span.colSpan, columns)
  });

  if (columns <= 2) {
    /** @type {{ colSpan: number, rowSpan: number, minHeight: number, imageObjectPosition: string }[]} */
    const mobilePatterns = [
      { colSpan: 1, rowSpan: 2, minHeight: 280, imageObjectPosition: 'center 22%' },
      { colSpan: 1, rowSpan: 1, minHeight: 148, imageObjectPosition: 'center' },
      { colSpan: 1, rowSpan: 1, minHeight: 148, imageObjectPosition: 'center' },
      { colSpan: 2, rowSpan: 1, minHeight: 160, imageObjectPosition: 'center 30%' },
      { colSpan: 2, rowSpan: 1, minHeight: 180, imageObjectPosition: 'center 22%' }
    ];
    if (total === 1) {
      return cap({ colSpan: 2, rowSpan: 2, minHeight: 300, imageObjectPosition: 'center 25%' });
    }
    if (total === 2) {
      return cap({ colSpan: 2, rowSpan: 1, minHeight: 220, imageObjectPosition: 'center 25%' });
    }
    return cap(mobilePatterns[index % mobilePatterns.length]);
  }

  /** @type {{ colSpan: number, rowSpan: number, minHeight: number, imageObjectPosition: string }[]} */
  const desktopPatterns = [
    { colSpan: 2, rowSpan: 2, minHeight: 280, imageObjectPosition: 'center 22%' },
    { colSpan: 1, rowSpan: 1, minHeight: 148, imageObjectPosition: 'center' },
    { colSpan: 1, rowSpan: 1, minHeight: 148, imageObjectPosition: 'center' },
    { colSpan: 2, rowSpan: 1, minHeight: 148, imageObjectPosition: 'center 30%' },
    { colSpan: 2, rowSpan: 1, minHeight: 200, imageObjectPosition: 'center 22%' }
  ];
  if (total === 1) {
    return cap({ colSpan: 4, rowSpan: 2, minHeight: 300, imageObjectPosition: 'center 25%' });
  }
  if (total === 2) {
    return cap({ colSpan: 2, rowSpan: 2, minHeight: 260, imageObjectPosition: 'center 25%' });
  }
  return cap(desktopPatterns[index % desktopPatterns.length]);
}

/**
 * @param {object[]} papers
 * @param {Record<string, { answers?: Record<string, string> }>} progressBySlug
 */
export function computePapersProgress(papers, progressBySlug) {
  let answered = 0;
  let total = 0;
  for (const paper of papers || []) {
    const paperTotal = flattenPaperQuestions(paper).length;
    total += paperTotal;
    const prog = progressBySlug?.[paper.slug];
    if (prog?.answers) {
      answered += countAnsweredInPaper(paper, prog.answers);
    }
  }
  return { answered, total };
}

/**
 * Progress across core ability papers (count of completed tests, not questions).
 * @param {Set<string> | string[]} completedSlugs
 * @param {string[]} coreSlugs
 */
export function computeCompletedAbilitiesProgress(completedSlugs, coreSlugs) {
  const set =
    completedSlugs instanceof Set ? completedSlugs : new Set(completedSlugs || []);
  let completed = 0;
  for (const slug of coreSlugs || []) {
    if (set.has(slug)) completed += 1;
  }
  return { completed, total: (coreSlugs || []).length };
}

/**
 * @param {object} paper
 * @param {Record<string, { answers?: Record<string, string> }>} progressBySlug
 */
export function getPaperProgress(paper, progressBySlug) {
  const total = flattenPaperQuestions(paper).length;
  const prog = progressBySlug?.[paper.slug];
  const answered = prog?.answers ? countAnsweredInPaper(paper, prog.answers) : 0;
  return { answered, total };
}

/** Tailwind classes for paper / section menu tiles — solid pastel (see abilityTheme.js). */
export const ABILITY_PAPER_TILE_COLORS = [
  'bg-[#A0C4FF]',
  'bg-[#B5EAD7]',
  'bg-[#D4C1EC]',
  'bg-[#FFD8BE]',
  'bg-[#FFB7C5]'
];

/** @deprecated use ABILITY_PAPER_TILE_COLORS */
export const ABILITY_SECTION_TILE_COLORS = [
  'bg-primary text-primary-foreground hover:bg-primary-hover focus-visible:ring-ring',
  'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-300',
  'bg-violet-600 text-white hover:bg-violet-700 focus-visible:ring-violet-300',
  'bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-amber-300',
  'bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-300',
  'bg-cyan-600 text-white hover:bg-cyan-700 focus-visible:ring-cyan-300',
  'bg-indigo-600 text-white hover:bg-indigo-700 focus-visible:ring-indigo-300',
  'bg-orange-600 text-white hover:bg-orange-700 focus-visible:ring-orange-300'
];

/**
 * @param {object} section
 * @param {string | null | undefined} passageId
 */
export function findPassageInSection(section, passageId) {
  if (!passageId || !section?.passages?.length) return null;
  return section.passages.find((p) => p.id === passageId) || null;
}

/**
 * @param {number} totalSeconds
 */
export function formatCountdown(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * Option keys used only for scoring / submissions (e.g. interest swipe). Hide in UI; show labels only.
 * @param {string | number} key
 */
export function isInternalTallyOptionKey(key) {
  const s = String(key);
  return s === '1' || s === '0' || s === '-1';
}

/** Matches `<main id="…">` in App.js — keyboard shortcuts use this to stay inside page content (nav/footer excluded). */
export const APP_MAIN_CONTENT_ID = 'app-main';
